import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { 
  User, 
  CheckCircle, 
  ArrowRight,
  TrendingUp,
  Shield,
  Clock,
  CreditCard,
  Zap,
  Target,
  Briefcase,
  Send
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const IndividualLoans = () => {
  const products = [
    {
      id: "sol_prop",
      icon: Target,
      title: "Sol Prop Loans",
      description: "A loan to individuals for productive purposes",
      rate: "6-10% per month",
      term: "1 - 12 months",
      amount: "$100 - $10,000",
      features: [
        "Quick approval process",
        "Flexible repayment terms",
        "No collateral required for small amounts",
        "Purpose-driven financing"
      ],
      applyLink: "/individual-loans/apply?product=sol_prop"
    },
    {
      id: "remittance",
      icon: Send,
      title: "Remittance Loans",
      description: "A loan granted against future remittances",
      rate: "5-8% per month",
      term: "1 - 6 months",
      amount: "$100 - $5,000",
      features: [
        "Advance on expected remittances",
        "Quick disbursement",
        "Flexible repayment tied to remittance",
        "No traditional collateral needed"
      ],
      applyLink: "/individual-loans/apply?product=remittance"
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Quick Approval",
      description: "Get approved within 24-48 hours with minimal documentation"
    },
    {
      icon: CreditCard,
      title: "Flexible Payments",
      description: "Choose a repayment term from 1 to 12 months"
    },
    {
      icon: Shield,
      title: "Transparent Terms",
      description: "Clear rates and no hidden fees"
    },
    {
      icon: Clock,
      title: "Easy Process",
      description: "Simple online application with fast disbursement"
    }
  ];

  const eligibility = [
    "Must be 18 years or older",
    "Valid national ID required",
    "Proof of income or business activity",
    "Bank account for disbursement",
    "Clean credit history preferred"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-white rounded-lg px-2 py-1 shadow-sm border border-gray-100">
                <img src={LOGO_URL} alt="Tzur Finances" className="h-8 object-contain" />
              </div>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/products" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Business Loans
              </Link>
              <Link to="/wholesale-lending" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Microfinance Wholesale Loans
              </Link>
              <Link to="/individual-loans" className="text-[#0A2540] font-medium">
                Individual Loans
              </Link>
              <Link to="/device-finance" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Device Finance
              </Link>
            </nav>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#0A2540] hover:bg-gray-100">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A2540] to-[#1e3a8a] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
            <User className="h-5 w-5 text-amber-400" />
            <span className="text-white text-sm font-medium">Individual Loans</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Personal Financing for Your Goals
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Access loans designed for individuals to achieve productive purposes with flexible terms and competitive rates
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/individual-loans/apply">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8">
                Apply Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/knowledge">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">$100 - $10K</p>
              <p className="text-gray-600 text-sm mt-1">Loan Amount</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">6-10%</p>
              <p className="text-gray-600 text-sm mt-1">Monthly Rate</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">1 - 12</p>
              <p className="text-gray-600 text-sm mt-1">Months Term</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">24-48hrs</p>
              <p className="text-gray-600 text-sm mt-1">Approval Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">Our Individual Loan Products</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Financing solutions tailored for individuals seeking productive purposes
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {products.map((product, index) => (
              <Card key={index} className="bg-white hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="h-14 w-14 rounded-full bg-[#0A2540]/10 flex items-center justify-center flex-shrink-0">
                      <product.icon className="h-7 w-7 text-[#0A2540]" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{product.title}</CardTitle>
                      <CardDescription className="text-base">{product.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Rate</p>
                      <p className="font-semibold text-[#0A2540]">{product.rate}</p>
                    </div>
                    <div className="text-center border-x border-gray-200">
                      <p className="text-sm text-gray-500">Term</p>
                      <p className="font-semibold text-[#0A2540]">{product.term}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Amount</p>
                      <p className="font-semibold text-[#0A2540]">{product.amount}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-6">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link to={product.applyLink || "/individual-loans/apply"}>
                    <Button className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90">
                      Apply for {product.title}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">Why Choose Us?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="font-semibold text-[#0A2540] mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0A2540] mb-6">Eligibility Requirements</h2>
              <p className="text-gray-600 mb-6">
                Check if you meet our basic requirements to apply for an individual loan:
              </p>
              <div className="space-y-3">
                {eligibility.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#0A2540] to-[#1e3a8a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Apply?</h3>
              <p className="text-gray-300 mb-6">
                Start your application today and get a decision within 24-48 hours. Our team is here to help you every step of the way.
              </p>
              <Link to="/individual-loans/apply">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white w-full">
                  Start Application
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">How It Works</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Apply Online", desc: "Complete our simple application form" },
              { step: "2", title: "Submit Documents", desc: "Upload required documents" },
              { step: "3", title: "Get Approved", desc: "Receive approval within 24-48 hours" },
              { step: "4", title: "Receive Funds", desc: "Get funds deposited to your account" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="h-12 w-12 rounded-full bg-[#0A2540] text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {item.step}
                </div>
                <h3 className="font-semibold text-[#0A2540] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/individual-loans/apply">
              <Button size="lg" className="bg-[#0A2540] hover:bg-[#0A2540]/90">
                Start Your Application
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">Tzur Finances is a registered microfinance institution</p>
          <p className="text-sm mt-2">© 2024 Tzur Finances. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default IndividualLoans;
